import WarningIcon from '@mui/icons-material/Warning'
import Button from '@mui/material/Button'
import DialogContentText from '@mui/material/DialogContentText'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { processServerErrorMessages } from 'global/helpers/form'
import { type ErrorMessage, getRequestErrorMessages } from 'global/helpers/requestError'
import i18n from 'i18n'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import ShipmentNumbersField from 'screens/StatusManager/ShipmentNumbersField'
import { TRIPS_QUERY_KEY } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import {
  TRIP_DETAILS_QUERY_KEY,
  tripLegsQueryOptions,
  tripLogsQueryOptions,
} from 'screens/TripDetails/TripDetails.service'
import {
  type AvailableStopStatus,
  type StopLegPair,
  WorkflowType,
} from 'screens/TripDetails/TripDetails.types'
import {
  getDefaultStatus,
  getDefaultStatusDate,
  getDefaultStatusTime,
  getTimezoneReference,
  getTruckZoneMessage,
  getUniqueAvailableStatuses,
  getUpdatedStopsForBulkUpdateStopStatus,
  updateTripLegsCacheWithUpdatedStops,
} from './StatusManager.helpers'
import { bulkUpdateStopStatus } from './StatusManager.service'
import type { BulkStatusUpdateFormValues } from './StatusManager.types'
import StatusUpdateFields from './StatusUpdateFields'

interface BulkStatusUpdateDialogPayload {
  trip: TripData
  stopLegPairs: StopLegPair[]
  weblinkToken?: string
  noRegularStatuses?: boolean
  noDiscrepancyStatuses?: boolean
}

interface BulkStatusUpdateDialogProps {
  payload: BulkStatusUpdateDialogPayload
  open: boolean
  onAction: () => void
  onError: (errors: ErrorMessage[]) => void
  onClose: () => void
}

export const getStatusUpdateLabel = (type: string): string => {
  switch (type) {
    case 'PUP':
      return i18n.t('screens.cs.trip_details.update_status.bulk_pickup_status')
    case 'CUS':
      return i18n.t('screens.cs.trip_details.update_status.bulk_customs_status')
    case 'DEL':
      return i18n.t('screens.cs.trip_details.update_status.bulk_delivery_status')
    default:
      return i18n.t('screens.cs.trip_details.update_status.bulk_status')
  }
}

const BulkStatusUpdateDialog = ({
  payload,
  open,
  onAction,
  onError,
  onClose,
}: BulkStatusUpdateDialogProps): ReactElement => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { t } = useTranslation()
  const location = useLocation()
  const [availableStatuses, setAvailableStatuses] = useState<AvailableStopStatus[]>([])
  const [availableStopLegPairs, setAvailableStopLegPairs] = useState<StopLegPair[]>([])
  const [useDeviceTime, setUseDeviceTime] = useState<boolean>()
  const form = useForm<BulkStatusUpdateFormValues>({
    criteriaMode: 'all',
  })
  const { watch, handleSubmit, reset, formState, setError } = form
  const watchedStatusId = watch('statusId')
  const [selectedStatus, setSelectedStatus] = useState<AvailableStopStatus>()
  const tripLogsQuery = useQuery({
    ...tripLogsQueryOptions(payload.trip.entityId, payload.weblinkToken),
    enabled: open,
  })
  const truckZoneMessage = getTruckZoneMessage(
    tripLogsQuery.data ?? [],
    payload.stopLegPairs[0]!.stop.type,
    payload.stopLegPairs[0]!.stop.wayPointCid,
  ) ?? undefined
  const queryClient = useQueryClient()
  const bulkUpdateStatusMutation = useMutation({
    mutationFn: (data: BulkStatusUpdateFormValues) =>
      bulkUpdateStopStatus(payload.trip.entityId, payload.stopLegPairs, data, payload.weblinkToken),
    onSuccess: async (_response, data, _result, context) => {
      const updatedStops = getUpdatedStopsForBulkUpdateStopStatus(payload.stopLegPairs, data)
      const queryKey = tripLegsQueryOptions(payload.trip.entityId, payload.weblinkToken).queryKey
      await updateTripLegsCacheWithUpdatedStops(updatedStops, queryKey, context.client)
      analyticsEvent('polestar_cs_bulk_status_updated', [
        getRouteName(location.pathname),
        data.paperless ? 'paperless' : 'regular',
      ])
    },
    onSettled: (_response, error) => {
      if (error !== null) {
        const errorMessages = getRequestErrorMessages(error)
        // NOTE: use onError() only if all errors relate to specific stops, and are not generic server errors
        if (!errorMessages.every((errorMessage) => errorMessage['waypointCid'] !== undefined)) {
          setError('root', processServerErrorMessages(error))
          return
        }
        onError(errorMessages)
      } else {
        onAction()
      }
      setTimeout(() => {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, payload.trip.entityId] }),
          queryClient.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
        ])
      }, 2000)
    },
  })

  const paperlessAvailable = useMemo(() => payload.stopLegPairs
    .map((stopLegPair) => stopLegPair.leg.paperlessAvailable)
    .every((condition) => condition), [payload.stopLegPairs])
  useEffect(() => {
    setUseDeviceTime(payload.stopLegPairs[0]!.leg.workflowType !== WorkflowType.SECURE_CLOUD_DEPLOYMENT && isMobile && !!payload.weblinkToken)
  }, [payload, isMobile])

  useEffect(() => {
    const availableStatuses = getUniqueAvailableStatuses([
      ...(!payload.noRegularStatuses
          ? payload.stopLegPairs.flatMap((stopLegPair) => stopLegPair.stop.availableRegularStatuses)
          : []
      ),
      ...(!payload.noDiscrepancyStatuses
          ? payload.stopLegPairs.flatMap((stopLegPair) => stopLegPair.stop.availableDiscrepancyStatuses)
          : []
      ),
    ])
    setAvailableStatuses(availableStatuses)
    reset({
      wayPointType: payload.stopLegPairs[0]!.stop.type,
      statusId: getDefaultStatus(availableStatuses),
      shipmentNumbers: [],
      createdAtDate: getDefaultStatusDate(payload.stopLegPairs[0]!.stop, payload.stopLegPairs[0]!.leg),
      createdAtTime: getDefaultStatusTime(payload.stopLegPairs[0]!.stop, payload.stopLegPairs[0]!.leg),
      paperless: isMobile && Boolean(payload.weblinkToken) && paperlessAvailable,
    })
  }, [payload])

  useEffect(() => {
    const selectedStatus = availableStatuses.find((status) => status.id === watchedStatusId)
    const availableStopLegPairs = selectedStatus
      ? payload.stopLegPairs.filter((stopLegPair) => stopLegPair.stop.availableStatuses.map((status) => status.id).includes(selectedStatus.id))
      : []
    setSelectedStatus(selectedStatus)
    setAvailableStopLegPairs(availableStopLegPairs)
  }, [watchedStatusId, availableStatuses])

  const onSubmit: SubmitHandler<BulkStatusUpdateFormValues> = (data: BulkStatusUpdateFormValues) => {
    if (useDeviceTime) {
      data.createdAt = undefined
    }
    bulkUpdateStatusMutation.mutate(data)
  }

  return (
    <FormProvider {...form}>
      <KNDialog
        open={open}
        onClose={onClose}
        closeLabel={t('general.cancel')}
        preventClosing={formState.isSubmitting || bulkUpdateStatusMutation.isPending}
        title={getStatusUpdateLabel(payload.stopLegPairs[0]!.stop.type)}
        actions={
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            loading={formState.isSubmitting || bulkUpdateStatusMutation.isPending}
            onClick={handleSubmit(onSubmit)}
          >
            {t('screens.cs.trip_details.update_status.update_status')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <KNDialogFormErrors errors={formState.errors?.root} />
        <DialogContentText component="div" mb={2}>
          {t('screens.cs.trip_details.update_status.bulk_status_update_disclaimer')}
        </DialogContentText>
        <Stack spacing={2}>
          <StatusUpdateFields
            availableStatuses={availableStatuses}
            showPaperless={paperlessAvailable && !!selectedStatus && selectedStatus.proofs !== 'DISABLED'}
            shipmentNumbers={
              <ShipmentNumbersField
                stopLegPairs={payload.stopLegPairs}
                availableStopLegPairs={availableStopLegPairs}
                bulkStatusUpdateOnly={selectedStatus?.bulkStatusUpdateOnly}
              />
            }
            truckZoneMessage={truckZoneMessage}
            timezoneReference={getTimezoneReference(payload.stopLegPairs[0]!.stop, payload.stopLegPairs[0]!.leg)}
            useDeviceTime={useDeviceTime}
            usePreciseTime={payload.stopLegPairs[0]!.leg.workflowType === WorkflowType.SECURE_CLOUD_DEPLOYMENT}
            weblinkToken={payload.weblinkToken}
          />
          {payload.stopLegPairs[0]!.stop.type === 'CUS' && (
            <KNCaption iconColor="warning" icon={<WarningIcon />}>
              {t('screens.cs.trip_details.update_status.customs_local_time')}
            </KNCaption>
          )}
          <KNCaption iconColor="warning" icon={<WarningIcon />}>
            {t('screens.cs.trip_details.update_status.warning')}
          </KNCaption>
        </Stack>
      </KNDialog>
    </FormProvider>
  )
}

export default BulkStatusUpdateDialog
